import type { AgentUsage } from '../types';

export type ResourceKind = 'staff' | 'room' | 'equipment';
export type ExceptionType = 'closed' | 'custom_hours';

export interface SyncCalendarShort {
  id: string;
  name: string;
  provider: string;
  provider_display: string;
  email: string;
}

export interface SchedulingProfile {
  id: string;
  organization: string;
  name: string;
  timezone: string;
  slot_granularity_minutes: number;
  min_lead_time_minutes: number;
  max_advance_days: number;
  sync_calendar: SyncCalendarShort | null;
  booking_page_slug: string | null;
  booking_page_enabled: boolean;
  send_confirmation_sms: boolean;
  reminder_24h_enabled: boolean;
  reminder_1h_enabled: boolean;
  reminder_text_de: string;
  reminder_text_en: string;
  is_active: boolean;
  agents: AgentUsage[];
  created: string;
  modified: string;
}

export interface SchedulingProfileInput {
  name?: string;
  slot_granularity_minutes?: number;
  min_lead_time_minutes?: number;
  max_advance_days?: number;
  sync_calendar?: string | null;
  booking_page_slug?: string | null;
  booking_page_enabled?: boolean;
  send_confirmation_sms?: boolean;
  reminder_24h_enabled?: boolean;
  reminder_1h_enabled?: boolean;
  reminder_text_de?: string;
  reminder_text_en?: string;
  is_active?: boolean;
}

export interface Resource {
  id: string;
  profile: string;
  name: string;
  kind: ResourceKind;
  kind_display: string;
  priority: number;
  is_active: boolean;
}

export interface ResourceInput {
  profile?: string;
  name?: string;
  kind?: ResourceKind;
  priority?: number;
  is_active?: boolean;
}

export interface ServiceVariant {
  id: string;
  service: string;
  name: string;
  duration_minutes: number;
  buffer_after_minutes: number | null;
  price: string | null;
}

export interface ServiceVariantInput {
  service?: string;
  name?: string;
  duration_minutes?: number;
  buffer_after_minutes?: number | null;
  price?: string | null;
}

export interface Service {
  id: string;
  profile: string;
  name: string;
  description: string;
  duration_minutes: number;
  buffer_after_minutes: number;
  price: string | null;
  resources: string[];
  variants: ServiceVariant[];
  is_active: boolean;
}

export interface ServiceInput {
  profile?: string;
  name?: string;
  description?: string;
  duration_minutes?: number;
  buffer_after_minutes?: number;
  price?: string | null;
  resources?: string[];
  is_active?: boolean;
}

export interface AvailabilityRule {
  id: string;
  profile: string | null;
  resource: string | null;
  weekday: number;
  is_closed: boolean;
  start_time: string | null;
  end_time: string | null;
}

export interface AvailabilityRuleInput {
  profile?: string | null;
  resource?: string | null;
  weekday?: number;
  is_closed?: boolean;
  start_time?: string | null;
  end_time?: string | null;
}

export interface AvailabilityException {
  id: string;
  profile: string | null;
  resource: string | null;
  date: string;
  date_end: string | null;
  type: ExceptionType;
  start_time: string | null;
  end_time: string | null;
  reason: string;
}

export interface AvailabilityExceptionInput {
  profile?: string | null;
  resource?: string | null;
  date?: string;
  date_end?: string | null;
  type?: ExceptionType;
  start_time?: string | null;
  end_time?: string | null;
  reason?: string;
}

export type BookingStatus = 'confirmed' | 'cancelled' | 'no_show';
export type BookingSource = 'phone_agent' | 'website' | 'manual';
export type BookingSyncStatus = 'not_required' | 'pending' | 'synced' | 'failed';

export interface Booking {
  id: string;
  resource: string;
  resource_name: string;
  service: string;
  service_name: string;
  variant: string | null;
  variant_name: string | null;
  start_datetime: string;
  end_datetime: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  source: BookingSource;
  source_display: string;
  status: BookingStatus;
  status_display: string;
  sync_status: BookingSyncStatus;
  sync_error: string;
  created: string;
}

export interface BookingListQuery {
  profile: string;
  page: number;
  pageSize: number;
  ordering: 'created' | '-created' | 'start_datetime' | '-start_datetime';
  resource?: string;
  status?: BookingStatus;
  source?: BookingSource;
  syncStatus?: BookingSyncStatus;
}

export interface BookingPage {
  count: number;
  results: Booking[];
}

export interface BookingCreateInput {
  service: string;
  variant?: string | null;
  resource: string;
  start_datetime: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
}

export interface BookingMoveInput {
  start_datetime: string;
  resource?: string;
}

// Deleting a profile, resource or service that bookings still point to.
export const isProtectedByBookings = (error: unknown): boolean =>
  error instanceof SchedulingApiError && error.code === 'protected_by_bookings';

// Raised for API errors so forms can show field-level messages.
export class SchedulingApiError extends Error {
  fieldErrors: Record<string, string>;
  nonFieldErrors: string[];
  status = 0;
  // Stable reason code from the API (e.g. "service_not_offered"), if it sent one.
  code?: string;

  constructor(
    message: string,
    fieldErrors: Record<string, string> = {},
    nonFieldErrors: string[] = [],
    code?: string
  ) {
    super(message);
    this.name = 'SchedulingApiError';
    this.fieldErrors = fieldErrors;
    this.nonFieldErrors = nonFieldErrors;
    this.code = code;
  }
}
